import React from 'react';
import { Drawer, Image, Spin } from 'antd';
import { Store, ImageOff, Inbox } from 'lucide-react';
import dayjs from 'dayjs';
import { EmptyState, StatusTag } from '../common';
import StoreCategoryDrawerController from './storeCategoryDrawer.controller';
import { sosStyle, titleize } from './sosHeat';

const imgUrl = (ref) => (!ref ? null : typeof ref === 'string' ? ref : ref.url || null);
const validImgs = (arr) => (arr || []).map(imgUrl).filter((u) => u && u !== '/not-found.png');

// SOS percentage pill, colored on the same scale as the table cells.
function SosPill({ pct }) {
    if (pct == null || Number.isNaN(pct)) return null;
    const { bg, color } = sosStyle(pct);
    return (
        <span className="inline-flex h-6 items-center rounded-md px-2 text-xs font-bold tabular-nums" style={{ background: bg, color }}>
            {pct.toFixed(1)}%
        </span>
    );
}

/** One shelf photo with its Antes / Después badge. */
const Photo = ({ src, tone = 'neutral', children }) => (
    <div className="relative overflow-hidden rounded-lg border border-surface-200">
        <Image src={src} width={104} height={104} style={{ objectFit: 'cover' }} />
        <span className="pointer-events-none absolute left-1 top-1">
            <StatusTag tone={tone} className="!h-5 !px-1.5 !text-[10px]">
                {children}
            </StatusTag>
        </span>
    </div>
);

/**
 * Drawer that lazy-loads a store's LATEST store-check by id and shows, per category, its
 * SOS (faces / totalFaces) alongside the shelf photos (before / after).
 */
export default class StoreCategoryDrawer extends StoreCategoryDrawerController {
    state = { loading: false, loadedId: null, check: null };

    renderCategory(cat, idx) {
        const sos = cat?.totalFaces > 0 ? ((cat.faces || 0) / cat.totalFaces) * 100 : null;
        const before = validImgs(cat?.imagesBefore);
        const after = validImgs(cat?.imagesAfter);
        return (
            <div key={cat.category || idx} className="border-b border-surface-100 py-4 last:border-b-0">
                <div className="mb-3 flex items-center justify-between gap-3">
                    <span className="font-semibold text-surface-900">{titleize(cat.category)}</span>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-surface-400 tabular-nums">
                            {cat.faces || 0}/{cat.totalFaces || 0} caras
                        </span>
                        <SosPill pct={sos} />
                    </div>
                </div>
                {before.length === 0 && after.length === 0 ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-surface-400">
                        <ImageOff />
                        Sin fotos para esta categoría.
                    </span>
                ) : (
                    <Image.PreviewGroup>
                        <div className="flex flex-wrap gap-2">
                            {before.map((u, i) => (
                                <Photo key={`b${i}`} src={u}>
                                    Antes
                                </Photo>
                            ))}
                            {after.map((u, i) => (
                                <Photo key={`a${i}`} src={u} tone="success">
                                    Después
                                </Photo>
                            ))}
                        </div>
                    </Image.PreviewGroup>
                )}
            </div>
        );
    }

    render() {
        const { open, onClose, storeName, clientName } = this.props;
        const { loading, check } = this.state;
        const cats = this.visibleCategories();

        return (
            <Drawer
                open={open}
                onClose={onClose}
                width={520}
                title={
                    <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-success-bg text-success-fg">
                            <Store />
                        </span>
                        <div className="min-w-0">
                            <div className="truncate font-semibold text-surface-900">{storeName || 'PDV'}</div>
                            {clientName ? <div className="truncate text-xs font-normal text-surface-500">{clientName}</div> : null}
                        </div>
                    </div>
                }>
                {loading ? (
                    <div className="flex justify-center py-16">
                        <Spin />
                    </div>
                ) : !check ? (
                    <EmptyState icon={<Inbox />} title="Sin datos" description="Sin datos del último levantamiento" />
                ) : (
                    <>
                        <div className="mb-1 rounded-lg border border-surface-200 bg-surface-25 px-4 py-3">
                            <h3 className="m-0 text-[15px] font-semibold text-surface-900">Último levantamiento</h3>
                            <span className="text-xs text-surface-500 tabular-nums">
                                {check.createdAt ? dayjs(check.createdAt).format('DD/MM/YYYY') : ''} · Share of Shelf por categoría
                            </span>
                        </div>
                        {cats.length === 0 ? (
                            <EmptyState icon={<Inbox />} title="Sin datos" description="Sin categorías con datos" />
                        ) : (
                            cats.map((c, i) => this.renderCategory(c, i))
                        )}
                    </>
                )}
            </Drawer>
        );
    }
}
